import React, { useMemo, useState } from "react";

import { useTranslation } from "utils";

import { CROP_TYPE_FCPO } from "models";

import { Button, ContextMenu, ContextMenuItemProps, Flex, Icon, IconButton, Tooltip, Typography } from "components";

export type BulkActionBarProps = {
  selectedCount: number;
  // duplicates allowed — distinct parent fields drive the enrolment copy/count
  selectedFieldIds: string[];
  onClear: () => void;
  // overrides the default "selected" wording, e.g. for a selection of empty slots
  selectionLabel?: string;
  // when set, the add-crops button takes this label and stands alone, without the add/edit data buttons
  addCropsLabel?: string;
  // when set (with onCopy), replaces every other right-side action with a single "copy" button
  copyLabel?: string;
  copyTooltip?: string;
  onCopy?: () => void;
  // hides the leading selectedCount number, for a selectionLabel that already states its own count
  hideSelectionCount?: boolean;
  // picks the crop-specific wording for the add-crops tooltip
  cropType?: CROP_TYPE_FCPO;
  onAddData?: () => void;
  onEditData?: () => void;
  // when passed, the matching button opens a menu instead of calling onAddData/onEditData directly
  addDataMenuItems?: ContextMenuItemProps[];
  editDataMenuItems?: ContextMenuItemProps[];
  isAddDataDisabled?: boolean;
  isEditDataDisabled?: boolean;
  onAddCrops?: () => void;
  // shows "Add crops" disabled with this reason as its tooltip; ignored when addCropsLabel is set
  addCropsDisabledReason?: string;
  onEnrollFields?: (fieldIds: string[]) => void;
  isEnrollFieldsDisabled?: boolean;
  onDeleteCrops?: (fieldIds: string[]) => void;
  isDeleteCropsDisabled?: boolean;
};

export const BulkActionBar = ({
  selectedCount,
  selectedFieldIds,
  onClear,
  selectionLabel,
  addCropsLabel,
  copyLabel,
  copyTooltip,
  onCopy,
  hideSelectionCount,
  cropType = CROP_TYPE_FCPO.MAIN,
  onAddData,
  onEditData,
  addDataMenuItems,
  editDataMenuItems,
  isAddDataDisabled,
  isEditDataDisabled,
  onAddCrops,
  addCropsDisabledReason,
  onEnrollFields,
  isEnrollFieldsDisabled,
  onDeleteCrops,
  isDeleteCropsDisabled,
}: BulkActionBarProps) => {
  const { t } = useTranslation(["fieldTimeline", "onboarding"]);

  const [isMoreActionsMenuVisible, setIsMoreActionsMenuVisible] = useState<boolean>(false);
  const [isAddDataMenuVisible, setIsAddDataMenuVisible] = useState<boolean>(false);
  const [isEditDataMenuVisible, setIsEditDataMenuVisible] = useState<boolean>(false);

  const parentFieldIds = useMemo(() => Array.from(new Set(selectedFieldIds)), [selectedFieldIds]);

  const moreActionsItems: ContextMenuItemProps[] = useMemo(() => {
    const items: ContextMenuItemProps[] = [];

    if (onEnrollFields && parentFieldIds.length > 0) {
      items.push({
        iconName: "tabler/layout-grid-add",
        label: t("multiSelection.enrollFields"),
        state: isEnrollFieldsDisabled ? ("disabled" as ContextMenuItemProps["state"]) : undefined,
        dataTestId: "bulk-action-bar-enroll-fields",
        onClick: isEnrollFieldsDisabled
          ? undefined
          : () => {
              setIsMoreActionsMenuVisible(false);
              onEnrollFields(parentFieldIds);
            },
        details: isEnrollFieldsDisabled ? (
          <Typography variant="paragraph-s">{t("onboarding:fieldDetails.carbonProgram.enrollmentDisabledDataSubmitted")}</Typography>
        ) : undefined,
      });
    }

    if (onDeleteCrops) {
      items.push({
        iconName: "tabler/trash",
        label: t("multiSelection.deleteCrops", { count: selectedCount }),
        state: isDeleteCropsDisabled ? ("disabled" as ContextMenuItemProps["state"]) : "warning",
        dataTestId: "bulk-action-bar-delete-crops",
        onClick: isDeleteCropsDisabled
          ? undefined
          : () => {
              setIsMoreActionsMenuVisible(false);
              onDeleteCrops(parentFieldIds);
            },
        details: isDeleteCropsDisabled ? <Typography variant="paragraph-s">{t("multiSelection.deleteCropsDisabledReason")}</Typography> : undefined,
      });
    }

    return items;
  }, [onEnrollFields, isEnrollFieldsDisabled, onDeleteCrops, isDeleteCropsDisabled, parentFieldIds, selectedCount, t]);

  const handleAddDataClick = () => {
    if (addDataMenuItems?.length) {
      setIsAddDataMenuVisible((isVisible) => !isVisible);
      return;
    }
    onAddData?.();
  };

  const handleEditDataClick = () => {
    if (editDataMenuItems?.length) {
      setIsEditDataMenuVisible((isVisible) => !isVisible);
      return;
    }
    onEditData?.();
  };

  const addCropsButton = onAddCrops && (
    <Tooltip placement="top" title={addCropsDisabledReason ?? t("multiSelection.addCropsTooltip", { context: cropType.toLowerCase() })}>
      <Button
        dataTestId="bulk-action-bar-add-crops-button"
        type="light primary"
        disabled={!!addCropsDisabledReason && !addCropsLabel}
        icon={<Icon className="inline" iconName="tabler/seeding" size="m-light" />}
        onClick={onAddCrops}
      >
        {addCropsLabel ?? t("multiSelection.addCrops")}
      </Button>
    </Tooltip>
  );

  return (
    <Flex data-testid="bulk-action-bar" className="bulk-action-bar" align="center" justify="space-between" gap="3rem">
      <Flex align="center" gap="0.5rem">
        {!hideSelectionCount && (
          <Typography variant="paragraph-m" className="selection-count no-wrap">
            {selectedCount}
          </Typography>
        )}
        <Typography variant="paragraph-m" className="selection-text no-wrap">
          {selectionLabel ?? t("multiSelection.selected")}
        </Typography>
        <IconButton
          dataTestId="bulk-action-bar-clear"
          className="bulk-action-bar-icon-button"
          iconName="tabler/x"
          type="tertiary"
          shape="square"
          size="small"
          tooltip={{ title: t("multiSelection.clear"), placement: "top" }}
          onClick={onClear}
        />
      </Flex>

      <Flex align="center" gap="0.75rem">
        {moreActionsItems.length > 0 && (
          <ContextMenu
            className="bulk-action-bar-menu"
            dataTestId="bulk-action-bar-more-actions"
            items={moreActionsItems}
            placement="top-left"
            iconSize="m-light"
            visible={isMoreActionsMenuVisible}
            onClose={() => setIsMoreActionsMenuVisible(false)}
          >
            <IconButton
              dataTestId="bulk-action-bar-more-actions-button"
              className="bulk-action-bar-icon-button"
              iconName="tabler/dots-vertical"
              type="tertiary"
              shape="square"
              size="small"
              tooltip={{ title: t("multiSelection.moreActions"), placement: "top" }}
              onClick={() => setIsMoreActionsMenuVisible((isVisible) => !isVisible)}
            />
          </ContextMenu>
        )}

        {copyLabel && onCopy ? (
          <Tooltip placement="top" title={copyTooltip}>
            <Button
              dataTestId="bulk-action-bar-copy-button"
              type="light primary"
              icon={<Icon className="inline" iconName="tabler/copy" size="m-light" />}
              onClick={onCopy}
            >
              {copyLabel}
            </Button>
          </Tooltip>
        ) : (
          <>
            {addCropsButton}

            {/* empty slots hold no data yet: the add-crops button stands alone in their bar */}
            {!addCropsLabel && (
              <>
                {(onAddData || addDataMenuItems?.length) && (
                  <ContextMenu
                    className="bulk-action-bar-menu"
                    dataTestId="bulk-action-bar-add-data"
                    items={addDataMenuItems ?? []}
                    placement="top-left"
                    iconSize="m-light"
                    visible={isAddDataMenuVisible}
                    onClose={() => setIsAddDataMenuVisible(false)}
                  >
                    <Tooltip placement="top" title={t("multiSelection.addDataTooltip")}>
                      <Button
                        dataTestId="bulk-action-bar-add-data-button"
                        type="light primary"
                        disabled={isAddDataDisabled}
                        icon={<Icon className="inline" iconName={addDataMenuItems?.length ? "tabler/chevron-down" : "klim/plus"} size="m-light" />}
                        onClick={handleAddDataClick}
                      >
                        {t("multiSelection.addData")}
                      </Button>
                    </Tooltip>
                  </ContextMenu>
                )}

                {(onEditData || editDataMenuItems?.length) && (
                  <ContextMenu
                    className="bulk-action-bar-menu"
                    dataTestId="bulk-action-bar-edit-data"
                    items={editDataMenuItems ?? []}
                    placement="top-right"
                    iconSize="m-light"
                    visible={isEditDataMenuVisible}
                    onClose={() => setIsEditDataMenuVisible(false)}
                  >
                    <Tooltip placement="top" title={t("multiSelection.editDataTooltip")}>
                      <Button
                        dataTestId="bulk-action-bar-edit-data-button"
                        type="light primary"
                        disabled={isEditDataDisabled}
                        icon={<Icon className="inline" iconName={editDataMenuItems?.length ? "tabler/chevron-down" : "klim/edit-element"} size="m-light" />}
                        onClick={handleEditDataClick}
                      >
                        {t("multiSelection.editData")}
                      </Button>
                    </Tooltip>
                  </ContextMenu>
                )}
              </>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
